/** Expenses — v1's expense system restored to v2. Three org-settings ARRAY
 * blobs, read here and written through the settings door (PHASE19), exactly like
 * Contractors (PHASE18):
 *   TXNEXP      per-deal transaction expenses  [{deal,date,desc,company,cat,amount,tax,status}]
 *   EXP2        property-expense ledger         v1 tuple rows [date,desc,cat,amount,pay,status,vendor?,...]
 *   COMPANY_EXP company/QBO expenses            [{id,dateIso,desc,cat,amt,tax,pay,st,vendor,notes,qboId,qboSync}]
 * WRITE is gated by liveFire `expensesEdit`. QBO connect/sync stays in v1 (OAuth). */
import type { RecOp } from "../../lib/outbox";
import { natKey } from "../../lib/natkey";
import { moneyExact } from "../../lib/money";
import { withLedgerBase } from "../settings/api";

// ---- shared helpers --------------------------------------------------------
const str = (v: unknown): string => (typeof v === "string" ? v : v == null ? "" : String(v));
const streetKey = (a: string): string => (natKey(a).split(",")[0] ?? "").trim();

/** Parse a v1 money string ("$1,234.50" | 1234.5 | "1,234") to a number. */
export function parseMoney(v: unknown): number {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  const cleaned = String(v ?? "").replace(/[^0-9.-]/g, "");
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : 0;
}

// ---- category / status vocab (v1 verbatim) ---------------------------------
export const EXP_CATS = [
  "Materials", "Labor", "Rehab", "Closing", "Selling", "Marketing", "Roof",
  "Plumbing", "Electrical", "Flooring", "HVAC", "Permits", "Utilities",
  "Insurance", "Title", "Other",
];
export const EXP_STATUS = ["PAID", "PENDING", "UNPAID"];
/** v1 Log-Expense (property) vocab, verbatim. */
export const LEX_PAY = ["None / N/A", "Zelle", "Wire", "Cash", "Check", "Card", "Mastercard", "Visa", "ACH", "Other"];
export const LEX_STATUS = ["Pending", "Approved", "Paid", "Unpaid"];
/** Company-expense categories = all property cats + business overhead (v1 LEX_CATS+). */
export const COMPANY_CATS = [
  ...EXP_CATS.slice(0, 15),
  "Software", "Office", "Travel", "Meals", "Fuel", "Phone", "Legal & Accounting", "Other",
];
export const PAY_METHODS = ["Cash", "Check", "Card", "ACH", "Wire", "Zelle", "Venmo", "Other"];

// ============================================================================
// 1. TXNEXP — per-deal transaction expenses (objects)
// ============================================================================
export interface TxnExpense {
  deal: string;
  date: string;
  desc: string;
  company: string;
  cat: string;
  amount: number;
  tax: number;
  status: string;
  /** client id stamped on rows v2 creates (2026-09-02) so two otherwise
   * identical expenses stay two rows through the ledger merge. Rows v1 or
   * older v2 wrote have none — that's fine, they're matched by content. */
  cid?: string;
}

export const newExpenseCid = (): string => "x" + Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

export function narrowTxnExpense(raw: unknown): TxnExpense {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  // parseMoney (not Number()) — a stored "10,026.73" string must never read as 0
  const amountRaw = parseMoney(o["amount"]);
  // v1 defaults tax (= total w/ tax) to the amount when blank
  const tax = o["tax"] == null || o["tax"] === "" ? amountRaw : parseMoney(o["tax"]);
  const cid = str(o["cid"]);
  return {
    deal: str(o["deal"]),
    date: str(o["date"]),
    desc: str(o["desc"]),
    company: str(o["company"]),
    cat: str(o["cat"]) || "Other",
    // mirror default: a row logged with ONLY "Total w/ Tax" filled stored
    // amount=0 — its cost is the total, not $0 (Tom's $10,026.73 closing costs)
    amount: amountRaw || tax,
    tax,
    status: str(o["status"]) || "PAID",
    ...(cid ? { cid } : {}),
  };
}

export function allTxnExpensesRaw(orgSettings: Record<string, unknown>): Record<string, unknown>[] {
  const v = orgSettings["TXNEXP"];
  return Array.isArray(v) ? (v as Record<string, unknown>[]) : [];
}

/** Expenses on ONE deal — full-address match with street-key fallback (matches
 * the Contractors reader; v1 blobs can carry slightly different formatting). */
export function txnExpensesForDeal(orgSettings: Record<string, unknown>, address: string): TxnExpense[] {
  const want = natKey(address);
  const wantStreet = streetKey(address);
  const out: TxnExpense[] = [];
  for (const r of allTxnExpensesRaw(orgSettings)) {
    const e = narrowTxnExpense(r);
    const dk = natKey(e.deal);
    if (dk === want || (wantStreet && streetKey(e.deal) === wantStreet)) out.push(e);
  }
  return out;
}

export function txnTotals(list: TxnExpense[]): { amount: number; tax: number } {
  return {
    amount: list.reduce((n, e) => n + e.amount, 0),
    tax: list.reduce((n, e) => n + e.tax, 0),
  };
}

export function txnExpenseToRaw(e: TxnExpense): Record<string, unknown> {
  return {
    deal: e.deal, date: e.date, desc: e.desc, company: e.company, cat: e.cat, amount: e.amount, tax: e.tax, status: e.status,
    ...(e.cid ? { cid: e.cid } : {}),
  };
}

export const opPutTxnExpenses = (all: Record<string, unknown>[]): RecOp =>
  withLedgerBase({ s: "settings", op: "put", k: "TXNEXP", d: all });

// ============================================================================
// 2. EXP2 — property-expense ledger (v1 tuple rows)
//    [0 date, 1 desc, 2 cat, 3 amount, 4 pay, 5 status, 6 vendor, ... 10 id, 11 dateIso]
// ============================================================================
export interface PropExpense {
  date: string;
  desc: string;
  cat: string;
  amount: number;
  pay: string;
  status: string;
  vendor: string;
  tax: number;
  invoice: string;
  id: string;
  dateIso: string;
  /** the property/transaction this expense is attributed to (v1 tuple slot 7,
   * previously left null). Free text = a property address; "" = unattributed. */
  property: string;
}

/** Money for display, EXACTLY as entered — cents survive ("$10,026.73"), whole
 * dollars stay clean ("$5,000"). Never rounds (Tom 2026-07-24). */
export const fmtMoneyExact = moneyExact;

/** Inverse of isoToDisplay: "Jul 24, 26" (or "Jul 24, 2026") -> "2026-07-24".
 * "" when unparseable — callers treat that as "no date". */
export function displayToIso(display: string): string {
  const m = /^([A-Za-z]{3})\s+(\d{1,2}),\s*(\d{2,4})$/.exec(display.trim());
  if (!m) return "";
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const mi = months.findIndex((x) => x.toLowerCase() === m[1]!.toLowerCase());
  if (mi < 0) return "";
  const yr = m[3]!.length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
  const day = Number(m[2]);
  if (!Number.isFinite(yr) || day < 1 || day > 31) return "";
  return `${yr}-${String(mi + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

/** A deal-ledger (TXNEXP) expense reshaped as a Property-Expenses row, so the
 * sidebar page shows BOTH ledgers (Tom: "I logged 3 expenses on the deal and
 * the Property Expenses page shows nothing"). property = the deal's address. */
export function dealExpenseAsLedgerRow(t: TxnExpense, idx: number): PropExpense {
  return {
    date: t.date, dateIso: displayToIso(t.date), desc: t.desc, cat: t.cat,
    amount: t.amount, tax: t.tax, pay: "\u2014", status: t.status,
    vendor: t.company, invoice: "", id: "txn:" + idx, property: t.deal,
  };
}

export function narrowPropExpense(raw: unknown): PropExpense {
  const t = Array.isArray(raw) ? (raw as unknown[]) : [];
  return {
    date: str(t[0]),
    desc: str(t[1]),
    cat: str(t[2]) || "Other",
    amount: parseMoney(t[3]),
    pay: str(t[4]),
    status: str(t[5]) || "Paid",
    vendor: str(t[6]),
    property: str(t[7]), // v1's reserved "transaction" slot — now the property link
    tax: parseMoney(t[12]),
    invoice: str(t[13]),
    id: str(t[10]),
    dateIso: str(t[11]),
  };
}

/** Back to a v1 EXP2 tuple, preserving the amount as v1's "$X,XXX" money string. */
export function propExpenseToRaw(e: PropExpense): unknown[] {
  // cents survive storage too — "$10,026.73" (was rounded to "$10,027")
  const money = moneyExact(e.amount);
  // slots: 7=property/transaction link · 8,9 legacy · 12=tax · 13=invoice# (v2 additive, back-compatible)
  return [e.date, e.desc, e.cat, money, e.pay, e.status, e.vendor, e.property || null, null, "", e.id, e.dateIso, e.tax || 0, e.invoice || ""];
}

export function allPropExpensesRaw(orgSettings: Record<string, unknown>): unknown[][] {
  const v = orgSettings["EXP2"];
  return Array.isArray(v) ? (v as unknown[][]) : [];
}

export function allPropExpenses(orgSettings: Record<string, unknown>): PropExpense[] {
  return allPropExpensesRaw(orgSettings).map(narrowPropExpense);
}

export const opPutPropExpenses = (all: unknown[][]): RecOp =>
  withLedgerBase({ s: "settings", op: "put", k: "EXP2", d: all });

// ============================================================================
// 3. COMPANY_EXP — company / QBO expenses (objects)
// ============================================================================
export interface CompanyExpense {
  id: string;
  dateIso: string;
  desc: string;
  cat: string;
  amt: number;
  tax: number;
  pay: string;
  st: string;
  vendor: string;
  notes: string;
  qboId: string;
  qboSync: string;
}

export function narrowCompanyExpense(raw: unknown): CompanyExpense {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  return {
    id: str(o["id"]),
    dateIso: str(o["dateIso"]),
    desc: str(o["desc"]),
    cat: str(o["cat"]) || "Other",
    amt: parseMoney(o["amt"]),
    tax: parseMoney(o["tax"]),
    pay: str(o["pay"]),
    st: str(o["st"]) || "Paid",
    vendor: str(o["vendor"]),
    notes: str(o["notes"]),
    qboId: str(o["qboId"]),
    qboSync: str(o["qboSync"]),
  };
}

export function allCompanyExpensesRaw(orgSettings: Record<string, unknown>): Record<string, unknown>[] {
  const v = orgSettings["COMPANY_EXP"];
  return Array.isArray(v) ? (v as Record<string, unknown>[]) : [];
}

export function allCompanyExpenses(orgSettings: Record<string, unknown>): CompanyExpense[] {
  return allCompanyExpensesRaw(orgSettings).map(narrowCompanyExpense);
}

export function companyExpenseToRaw(e: CompanyExpense): Record<string, unknown> {
  return {
    id: e.id, dateIso: e.dateIso, desc: e.desc, cat: e.cat, amt: e.amt, tax: e.tax,
    pay: e.pay, st: e.st, vendor: e.vendor, notes: e.notes,
    qboId: e.qboId || null, qboSync: e.qboSync,
  };
}

/** v1's month/YTD/total tiles (companyExpView). year/month are 0-based month. */
export function companyMonthYtd(list: CompanyExpense[], year: number, month: number): { month: number; ytd: number; total: number } {
  let m = 0, y = 0, total = 0;
  for (const e of list) {
    const a = e.amt + e.tax;
    total += a;
    const d = new Date(e.dateIso || 0);
    if (d.getFullYear() === year) {
      y += a;
      if (d.getMonth() === month) m += a;
    }
  }
  return { month: m, ytd: y, total };
}

export const opPutCompanyExpenses = (all: Record<string, unknown>[]): RecOp =>
  withLedgerBase({ s: "settings", op: "put", k: "COMPANY_EXP", d: all });

// ---- CSV (pure builder; the DOM download wrapper lives in the page) --------
export function buildExpenseCsv(header: string[], rows: (string | number)[][]): string {
  const esc = (v: string | number): string => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  return [header, ...rows].map((r) => r.map(esc).join(",")).join("\r\n");
}

// ---- id + display-date helpers (v1 parity) ---------------------------------
export function expenseId(seq: number): string {
  return "ce_" + seq.toString(36);
}
/** v1 stores the display date "Jul 31, 26"; ISO in comes from a <input type=date>. */
export function isoToDisplay(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" });
}

/** The one thing still missing before an expense can be added — null when
 * the form is complete. Shared by the deal form and the Property Expenses
 * form so both say the same words. (2026-09-02: the Add button used to go
 * silently inert instead — "i clicked add and nothing happened".) */
export function expenseBlocker({ desc, amount, cat }: { desc: string; amount: string; cat?: string }): string | null {
  if (desc.trim() === "") return "Type a description first (what was this for?).";
  const cleaned = amount.replace(/[^0-9.]/g, "");
  const a = Number(cleaned);
  if (amount.trim() !== "" && (cleaned === "" || !Number.isFinite(a))) return "Amount isn't a number — digits only, like 1500 or 1,500.50.";
  if (a < 0) return "Amount can't be negative.";
  if (cat !== undefined && cat === "") return "Type the new category name (or pick one from the list).";
  return null;
}

export const EXPENSE_MSG = {
  editPhaseB: "Logging expenses unlocks at go-live",
  none: "No expenses yet.",
  saveFail: "Couldn't save — nothing changed",
  notLoaded: "Couldn't save — your settings are still loading. Wait a moment and press Add again; nothing was lost.",
  qboV1: "QuickBooks connect & sync stays in v1 (OAuth).",
};
